/**
 * Persist settings changes from the modal.
 */
import {
  sanitizePercentage,
  sanitizeCurrency,
  formatPercentageInput,
  formatCurrencyInput,
} from '../../modules/formatting';
import { normalizeHomeZipPostal } from '../../modules/utils';
import { ensureSettings, saveSettings as saveSettingsToStorage } from '../../modules/state';
import type { Settings } from '../../modules/types';
import type { SettingsHandlerDeps } from './types';
import { applyNotificationPreferences } from './notificationPreferences';
import { syncProfile } from './profileSync';

function clearDistanceCache(): void {
  const windowWithCache = window as Window & {
    __distanceCache?: Record<string, unknown>;
    __distanceCalculationsStarted?: Set<unknown>;
  };
  windowWithCache.__distanceCache = {};
  if (typeof windowWithCache.__distanceCalculationsStarted !== 'undefined') {
    windowWithCache.__distanceCalculationsStarted.clear();
  }
}

function parseNotifyLeadMinutes(raw: string): number[] {
  const parsed = raw
    .split(/[\,\s]+/)
    .map(v => Number(v.trim()))
    .filter(n => Number.isFinite(n) && n > 0 && n < 24 * 60);

  const unique: number[] = [];
  for (const m of parsed) {
    if (!unique.includes(m)) unique.push(m);
  }
  unique.sort((a, b) => b - a);
  return unique.length ? unique : [60, 30, 10];
}

function updateNumericSettings(els: SettingsHandlerDeps['els'], settings: Settings): void {
  if (els.settingsTaxRate) {
    const nextTax = sanitizePercentage(els.settingsTaxRate.value, 0);
    settings.taxRate = nextTax;
    els.settingsTaxRate.value = formatPercentageInput(nextTax);
  }

  if (els.settingsBidderPremium) {
    const nextPremium = sanitizePercentage(els.settingsBidderPremium.value, 15);
    settings.bidderPremium = nextPremium;
    els.settingsBidderPremium.value = formatPercentageInput(nextPremium);
  }

  if (els.settingsSellerCosts) {
    const nextSellerCosts = sanitizePercentage(els.settingsSellerCosts.value, 14);
    settings.sellerCosts = nextSellerCosts;
    els.settingsSellerCosts.value = formatPercentageInput(nextSellerCosts);
  }

  if (els.settingsFixedLotCost) {
    const nextFixedCost = sanitizeCurrency(els.settingsFixedLotCost.value, 0);
    settings.fixedLotCost = nextFixedCost;
    els.settingsFixedLotCost.value = formatCurrencyInput(nextFixedCost);
  }
}

function updateDistanceUnits(els: SettingsHandlerDeps['els'], settings: Settings): void {
  if (!els.settingsDistanceUnits) return;

  const duVal = (els.settingsDistanceUnits.value || 'auto').toLowerCase();
  settings.distanceUnits = duVal === 'mi' || duVal === 'km' ? duVal : 'auto';
  els.settingsDistanceUnits.value = settings.distanceUnits;
}

export async function saveSettingsFromModal(deps: SettingsHandlerDeps): Promise<void> {
  const { state, els, cfg, render, showToast, upsertUserProfile, lastProfileSaveStatus } = deps;

  const settings = ensureSettings();
  const oldZipCode = settings.zipCode;

  if (els.settingsTz) {
    const tz = els.settingsTz.value || '';
    settings.timeZone = tz || undefined;
  }

  if (els.settingsZip) {
    const zipValue = (els.settingsZip.value || '').trim();
    const normalized = normalizeHomeZipPostal(zipValue);
    settings.zipCode = normalized;
    els.settingsZip.value = settings.zipCode;

    if (oldZipCode !== settings.zipCode) {
      clearDistanceCache();
      if (state.mode === 'auth0' && state.extAccessToken) {
        try {
          await upsertUserProfile(cfg, { distanceCache: {} });
        } catch {
          // Silently fail
        }
      }
    }
  }

  updateDistanceUnits(els, settings);
  updateNumericSettings(els, settings);

  if (els.settingsNotifyLeadMinutes) {
    settings.notifyLeadMinutes = parseNotifyLeadMinutes(els.settingsNotifyLeadMinutes.value || '');
    els.settingsNotifyLeadMinutes.value = settings.notifyLeadMinutes.join(',');
  }

  await applyNotificationPreferences(deps, settings);

  const profileResult = await syncProfile(deps);

  saveSettingsToStorage();

  if (profileResult.attempted && !profileResult.success) {
    if (state.mode !== 'auth0') {
      showToast('Settings saved; redirecting to sign in to update your profile…', {
        variant: 'warning',
        duration: 4000,
      });
    } else if (lastProfileSaveStatus.value === 404 || lastProfileSaveStatus.value === 0) {
      showToast('Settings saved; profile service is not available right now (404).', {
        variant: 'warning',
        duration: 4500,
      });
    } else {
      showToast('Settings saved; profile not updated.', { variant: 'warning', duration: 3500 });
    }
  } else if (profileResult.attempted) {
    showToast('Settings saved; company updated.', { variant: 'success', duration: 2500 });
  } else {
    showToast('Settings saved', { variant: 'success', duration: 2500 });
  }

  render();
  // Options is now a separate page - no need to toggle card visibility
}
